/**
 * Migrated HurryTimer countdowns inside page bodies (tracker §3R LT-19).
 *
 * `snippets/zg-countdown.liquid` renders a box per `[hurrytimer]` mount and
 * this fills it, once a second, exactly as live's own plugin script does:
 * WordPress serves an EMPTY timer row and writes every digit in the browser
 * (hurrytimer-pro/assets/js/hurrytimer.js). Nothing here is migrated markup —
 * the digits do not exist until this runs, on live or here.
 *
 * TWO MODES, both read from the campaign's `wp_postmeta` at extract time:
 *
 *   daily  `mode = 3` recurring, `recurring_frequency = daily`,
 *          `_hurryt_recurring_duration = 24h`. The campaign counts to the next
 *          occurrence of `recurring_start_time`'s TIME OF DAY in the WordPress
 *          site timezone and then **restarts, every day, forever** — the user's
 *          2026-09-14 decision is to keep that exactly as it is. Three of the
 *          five campaigns in use are this, all at 02:00 America/Chicago.
 *   fixed  `mode = 1` regular, a single `end_datetime`. Two campaigns, both
 *          already past (2023-03-13 and 2025-02-06).
 *
 * THE ONE-SECOND OFFSET IS LIVE'S, NOT A ROUNDING BUG. For a recurring
 * campaign the plugin's target is `start + duration - 1` second
 * (`Campaign::getRecurrenceEndDate()`), so a 02:00 daily campaign reaches zero
 * at 01:59:59 and rolls. A fixed campaign's target is its `end_datetime`
 * exactly. Both are reproduced.
 *
 * DST IS ASKED OF `Intl`, NOT TABULATED — the same approach
 * `assets/zager-promo-banner.js` uses for the footer banner's countdown (which
 * is the same plugin, campaign 160542, resetting at MIDNIGHT rather than
 * 02:00). Read the wall clock in the zone, work out how far it is from the
 * reset time, and let the zone's own rules do the rest.
 *
 * AT ZERO. A daily campaign never expires; it rolls to tomorrow. A fixed one
 * does what its campaign's end action says: `hide` is `C::ACTION_HIDE`, which
 * every campaign in use is set to, and `zero` freezes the display at `00`s.
 *
 * `type="module"`, so a page carrying two countdowns loads this once.
 */

const ROOT_SELECTOR = '[data-zg-countdown]';
const SECOND = 1000;

/** Seconds per unit, largest first. `months` is deliberately absent — the
 *  transform refuses a campaign that shows one, because a calendar month has
 *  no fixed length and guessing 30 days would drift against live. */
const UNIT_SECONDS = {
  days: 86400,
  hours: 3600,
  minutes: 60,
  seconds: 1,
};

/**
 * The wall clock in `timeZone`, as seconds since that zone's midnight.
 *
 * @param {Date} now
 * @param {string} timeZone - IANA zone name.
 * @returns {number} Seconds elapsed today in that zone.
 */
function secondsIntoDay(now, timeZone) {
  let parts;
  try {
    parts = new Intl.DateTimeFormat('en-US', {
      timeZone,
      hour12: false,
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    }).formatToParts(now);
  } catch {
    // An unknown zone must not stop the clock — fall back to the visitor's.
    parts = new Intl.DateTimeFormat('en-US', {
      hour12: false,
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    }).formatToParts(now);
  }
  const at = (type) => Number(parts.find((p) => p.type === type)?.value ?? 0);
  // `hour12: false` renders midnight as 24 in some ICU versions.
  return (at('hour') % 24) * 3600 + at('minute') * 60 + at('second');
}

/** `02:00:00` -> 7200. Returns NaN for anything else. */
function resetSeconds(value) {
  const m = /^(\d{1,2}):(\d{2})(?::(\d{2}))?$/.exec(String(value ?? '').trim());
  if (!m) return NaN;
  return Number(m[1]) * 3600 + Number(m[2]) * 60 + Number(m[3] ?? 0);
}

/**
 * Milliseconds until this campaign reaches zero.
 *
 * @param {HTMLElement} root
 * @returns {number} Milliseconds; <= 0 means the campaign has ended.
 */
function remainingMs(root) {
  const now = new Date();

  if (root.dataset.zgMode === 'daily') {
    const reset = resetSeconds(root.dataset.zgReset);
    if (Number.isNaN(reset)) return -1;
    const elapsed = secondsIntoDay(now, root.dataset.zgTimezone || 'America/Chicago');
    // Distance to the NEXT occurrence of the reset time, then live's own
    // `- 1 second` (the plugin's `start + duration - 1`).
    let ahead = reset - elapsed;
    if (ahead <= 0) ahead += 86400;
    return (ahead - 1) * SECOND - now.getMilliseconds();
  }

  const target = Date.parse(root.dataset.zgEnds || '');
  if (Number.isNaN(target)) return -1;
  return target - now.getTime();
}

/**
 * Build the blocks once, so each tick only rewrites the numbers.
 *
 * @param {HTMLElement} root
 * @returns {HTMLElement[]} One digit element per unit, in marker order.
 */
function build(root) {
  const timer = root.querySelector('[data-zg-countdown-timer]');
  if (!timer) return [];
  if (timer.childElementCount) {
    return Array.from(timer.querySelectorAll('.zg-countdown__digit'));
  }

  const units = (root.dataset.zgUnits || '').split(',').filter(Boolean);
  const labels = (root.dataset.zgLabels || '').split(',');
  const separated = root.dataset.zgSeparator !== 'off';
  const digits = [];

  units.forEach((unit, index) => {
    if (index > 0 && separated) {
      const sep = document.createElement('div');
      sep.className = 'zg-countdown__sep';
      sep.setAttribute('aria-hidden', 'true');
      sep.textContent = ':';
      timer.append(sep);
    }

    const block = document.createElement('div');
    block.className = 'zg-countdown__block';

    const digit = document.createElement('div');
    digit.className = 'zg-countdown__digit';
    digit.textContent = '00';
    block.append(digit);
    digits.push(digit);

    const label = labels[index];
    if (label) {
      const caption = document.createElement('div');
      caption.className = 'zg-countdown__label';
      caption.textContent = label;
      block.append(caption);
    }

    timer.append(block);
  });

  return digits;
}

/**
 * One tick: compute, render, and decide whether to keep going.
 *
 * @param {HTMLElement} root
 * @returns {boolean} False when this countdown is finished with.
 */
function tick(root) {
  const left = remainingMs(root);
  const ended = left <= 0;

  if (ended && root.dataset.zgMode !== 'daily' && root.dataset.zgExpired !== 'zero') {
    // `C::ACTION_HIDE`: the campaign's own end action, which is what all five
    // in use are set to.
    root.hidden = true;
    return false;
  }

  const digits = build(root);
  const units = (root.dataset.zgUnits || '').split(',').filter(Boolean);
  let rest = ended ? 0 : Math.floor(left / SECOND);

  units.forEach((unit, index) => {
    const size = UNIT_SECONDS[unit] ?? 1;
    // The LARGEST VISIBLE unit absorbs everything above it, which is what the
    // plugin's `%I` / `%N` directives do when a bigger block is switched off:
    // a campaign showing hours:minutes:seconds prints TOTAL hours.
    const value = Math.floor(rest / size);
    rest -= value * size;
    const digit = digits[index];
    if (digit) digit.textContent = String(value).padStart(2, '0');
  });

  // A daily campaign rolls; a zeroed fixed one has nothing left to say.
  return !ended || root.dataset.zgMode === 'daily';
}

function start(root) {
  if (root.dataset.zgCountdownStarted === '1') return;
  root.dataset.zgCountdownStarted = '1';

  if (!tick(root)) return;
  const id = window.setInterval(() => {
    if (!root.isConnected || !tick(root)) window.clearInterval(id);
  }, SECOND);
}

function startAll(scope = document) {
  scope.querySelectorAll(ROOT_SELECTOR).forEach((root) => start(root));
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => startAll());
} else {
  startAll();
}

// The theme editor re-renders a section without reloading the page.
document.addEventListener('shopify:section:load', (event) => {
  const target = event.target;
  if (target instanceof HTMLElement) startAll(target);
});
